// On-screen pagination for the declaration paper (design doc §8): the paper
// is ONE document that reads like the PDF's pages. A measure → plan → apply
// pass splits it into sheets — a gap wherever content would run past a
// sheet's content box, the backdrop band (sheetBands.ts) drawn over each gap
// — so the preview breaks where Gotenberg's Chromium does. Forced
// `.pagebreak` elements are just one of the reasons the planner emits a gap;
// natural overflow, annexes and table rows are the others.

import { renderBands } from "./sheetBands"

const MM = 96 / 25.4
export const PAGE_HEIGHT_PX = 297 * MM
/** The backdrop band between two sheets. */
export const PAGE_GAP_PX = 24

// ---------------------------------------------------------------------------
// Pure layout planner (design doc §8). Given the natural (pre-gap) positions
// of a paper's top-level blocks, works out where sheet gaps need to go so
// the on-screen pagination mirrors Chromium's (and so the PDF's) page
// breaks. Pure and side-effect free — measuring the DOM and applying the
// resulting gaps are separate steps that live elsewhere.

/** Sheet geometry in px: the full sheet height, the content margins
 *  top/bottom, and the backdrop-band gap drawn between sheets. */
export interface SheetGeometry {
  pageHeightPx: number
  topPx: number
  bottomPx: number
  gapPx: number
}

export type BlockKind = "block" | "annex" | "heading" | "pagebreak" | "strip"

/** A table row's natural top/bottom, in the same (pre-gap) coordinate space
 *  as its parent `MeasuredBlock`. Rows never split across sheets. */
export interface MeasuredRow {
  top: number
  bottom: number
}

/** A top-level block's natural position (px from the paper's top edge,
 *  before any gaps the planner adds) and pagination metadata. `rows`, when
 *  present, lets the planner break inside the block at a row boundary
 *  instead of moving it whole; `headHeight` reserves room above a moved
 *  row for the table's repeated head. */
export interface MeasuredBlock {
  index: number
  top: number
  bottom: number
  kind: BlockKind
  rows?: MeasuredRow[]
  headHeight?: number
}

export type Break =
  | { atBlock: number; gapPx: number }
  | { atBlock: number; atRow: number; gapPx: number }

/** Tolerance for "is this block already sitting at a sheet's content top". */
const SHEET_TOP_EPSILON_PX = 0.5

const strideOf = (geometry: SheetGeometry): number =>
  geometry.pageHeightPx + geometry.gapPx

/** Sheet `sheet`'s content box is `[contentTopOf(sheet), contentBottomOf(sheet))`. */
const contentTopOf = (sheet: number, geometry: SheetGeometry): number =>
  sheet * strideOf(geometry) + geometry.topPx

const contentBottomOf = (sheet: number, geometry: SheetGeometry): number =>
  sheet * strideOf(geometry) + geometry.pageHeightPx - geometry.bottomPx

/** The gap that, inserted right at `effectiveTop` (an already-shifted
 *  position on `sheet`), pushes whatever starts there onto the next sheet,
 *  landing it exactly on that sheet's content top
 *  (`(sheet + 1) * stride + topPx`): the rest of `sheet`'s content box, its
 *  bottom margin, the gap band, then the next sheet's top margin. `extra`
 *  reserves additional room above it (a repeated table head on a row-split
 *  break). */
function gapToNextSheet(
  sheet: number,
  effectiveTop: number,
  geometry: SheetGeometry,
  extra = 0,
): number {
  return (
    contentBottomOf(sheet, geometry) -
    effectiveTop +
    geometry.bottomPx +
    geometry.gapPx +
    geometry.topPx +
    extra
  )
}

/** Plans where sheet gaps go for a paper's top-level blocks, measured in
 *  their natural (pre-gap) positions, so the preview's pagination mirrors
 *  Chromium's fragmentation (design doc §8):
 *   - a block without `rows` that doesn't fit moves whole to the next sheet;
 *   - a block with `rows` breaks at the first row that doesn't fit (moving
 *     just the tail, head reserved via `headHeight`), unless that is the
 *     first row, in which case the whole block moves;
 *   - `annex` blocks always start a fresh sheet, unless already at one;
 *   - `heading` blocks never end a sheet: if the block(s) following a run
 *     of headings must break, the break is emitted at the run's first
 *     heading instead, so the whole run travels together;
 *   - `pagebreak` blocks always consume the rest of their sheet;
 *   - `strip` blocks are ignored entirely (zero height, never break).
 *  Returns at most one `Break` per block, sorted by `atBlock`/`atRow`. */
export function planBreaks(
  blocks: MeasuredBlock[],
  geometry: SheetGeometry,
): Break[] {
  const breaks: Break[] = []
  let shift = 0
  let sheet = 0
  // First block of a run of consecutive headings that hasn't been attached
  // to a break yet — headings never end a sheet, so they travel with
  // whatever follows if that needs to break.
  let headingRunStart: MeasuredBlock | null = null

  /** Moves `block` (or, if it's preceded by a still-open heading run, the
   *  whole run) to the start of the next sheet. */
  const moveWhole = (block: MeasuredBlock): void => {
    const target = headingRunStart ?? block
    const gapPx = gapToNextSheet(sheet, target.top + shift, geometry)
    breaks.push({ atBlock: target.index, gapPx })
    shift += gapPx
    sheet += 1
    // `target` has now been spent — its break is committed, so it can never
    // be reused as a redirect target again (that would emit a second Break
    // for the same atBlock). If `block` (the one that *triggered* this
    // move, possibly by redirect) is itself a heading, it becomes the new,
    // still-unbroken, run anchor for whatever follows it on the new sheet;
    // anything else closes the run.
    headingRunStart = block.kind === "heading" ? block : null
  }

  for (const block of blocks) {
    if (block.kind === "strip") continue

    if (block.kind === "pagebreak") {
      // A forced break always breaks at its own position — CSS
      // `break-before: always` (what `.pagebreak` models) overrides a
      // preceding heading's `break-after: avoid`, so a fitting heading
      // right before it is left behind on the current sheet, not dragged
      // along. Bypasses `moveWhole`'s heading-run redirect on purpose.
      const gapPx = gapToNextSheet(sheet, block.top + shift, geometry)
      breaks.push({ atBlock: block.index, gapPx })
      shift += gapPx
      sheet += 1
      headingRunStart = null
      continue
    }

    if (block.kind === "annex") {
      const effectiveTop = block.top + shift
      const atSheetTop =
        Math.abs(effectiveTop - contentTopOf(sheet, geometry)) <=
        SHEET_TOP_EPSILON_PX
      if (!atSheetTop) {
        moveWhole(block)
        continue
      }
    }

    const contentBottom = contentBottomOf(sheet, geometry)
    const rows = block.rows

    if (rows && rows.length > 0) {
      const overflowRow = rows.findIndex(
        (row) => row.bottom + shift > contentBottom,
      )
      if (overflowRow === 0) {
        moveWhole(block)
        continue
      }
      if (overflowRow > 0) {
        const row = rows[overflowRow]
        const gapPx = gapToNextSheet(
          sheet,
          row.top + shift,
          geometry,
          block.headHeight ?? 0,
        )
        // At most one Break per block: if the moved tail (rows from
        // `overflowRow` on) is itself taller than the next sheet's content
        // box too, that residual overflow is left unresolved by this call
        // — it's picked up by the caller's next (debounced, idempotent)
        // planning pass once the DOM reflects this break, not split further
        // here.
        breaks.push({ atBlock: block.index, atRow: overflowRow, gapPx })
        shift += gapPx
        sheet += 1
        headingRunStart = null
        continue
      }
      // overflowRow === -1: every row fits on the current sheet.
    } else if (block.bottom + shift > contentBottom) {
      moveWhole(block)
      continue
    }

    // The block fit on the current sheet without a break.
    headingRunStart =
      block.kind === "heading" ? (headingRunStart ?? block) : null
  }

  return breaks.sort((a, b) => {
    if (a.atBlock !== b.atBlock) return a.atBlock - b.atBlock
    const rowA = "atRow" in a ? a.atRow : -1
    const rowB = "atRow" in b ? b.atRow : -1
    return rowA - rowB
  })
}

/** `SheetGeometry` for an A4 sheet (297mm) from the draft's page margins
 *  (`page_margins_mm`, in millimetres); the gap band is the same constant
 *  the forced-break pass above uses. */
export function geometryFrom(marginsMm: {
  top: number
  bottom: number
}): SheetGeometry {
  return {
    pageHeightPx: PAGE_HEIGHT_PX,
    topPx: marginsMm.top * MM,
    bottomPx: marginsMm.bottom * MM,
    gapPx: PAGE_GAP_PX,
  }
}

/** The geometry of a document that brings no page margins of its own: the
 *  `.paper` padding declaration_v2.css's `@media screen` block writes
 *  (`12mm 15mm 20mm`). A module constant, not a fresh object per render, so
 *  it can be a component's default prop without re-running effects. */
export const DEFAULT_SHEET_GEOMETRY = geometryFrom({ top: 12, bottom: 20 })

// ---------------------------------------------------------------------------
// DOM side of the pass: measure the paper's top-level blocks, plan, apply.

/** Spacer row a row-split break inserts before the first row of the tail. */
const GAP_ROW_CLASS = "sheet-gap"
/** The table head repeated above that tail (a clone, never part of the tree). */
const HEAD_ROW_CLASS = "sheet-head"
/** The stylesheet that holds this paper's sheet gaps, beside the paper. */
const GAP_STYLE_CLASS = "sheet-gaps"
/** Tells one paper's gap rules from another's when two are on the page. */
let paperSeq = 0
/** The space between a page strip and the text column — the same 6mm the
 *  band clones use on screen (`.sheet-strip-clone` padding) and
 *  `page_margins_mm` reserves for the PDF (`STRIP_GAP_MM`). */
const STRIP_GAP_PX = 6 * MM

/** Moves a measured block (and its rows) down by `offsetPx`. */
const shifted = (block: MeasuredBlock, offsetPx: number): MeasuredBlock => ({
  ...block,
  top: block.top + offsetPx,
  bottom: block.bottom + offsetPx,
  ...(block.rows
    ? {
        rows: block.rows.map((row) => ({
          top: row.top + offsetPx,
          bottom: row.bottom + offsetPx,
        })),
      }
    : {}),
})

/** The blocks the pass paginates: the paper's own top-level children (the
 *  bands live in an overlay beside it). Their order IS `MeasuredBlock.index`. */
const contentChildren = (paper: HTMLElement): HTMLElement[] =>
  Array.from(paper.children).filter(
    (el): el is HTMLElement => el instanceof HTMLElement,
  )

const has = (el: HTMLElement, selector: string): boolean =>
  el.matches(selector) || !!el.querySelector(selector)

/** A forced break: the `.pagebreak` element itself (the template editor's
 *  NodeView) or a wrapper holding nothing but one (the conformity editor's
 *  locked fragment around `<div class="pagebreak">`). */
const isPageBreak = (el: HTMLElement): boolean =>
  el.classList.contains("pagebreak") ||
  (el.querySelectorAll(".pagebreak").length === 1 &&
    !(el.textContent ?? "").trim())

function kindOf(el: HTMLElement): BlockKind {
  if (has(el, "[data-page-strip]")) return "strip"
  // An annex the template lets follow on (`.annex-flow`) is a plain block.
  if (has(el, ".annex:not(.annex-flow)")) return "annex"
  // Only the block's OWN class/tag: a framework block wraps `.h2`/`.eyb`
  // headings of its own and is a plain block, not a heading.
  if (el.matches("h1, h2, h3, .h2, .h3, .eyb")) return "heading"
  if (isPageBreak(el)) return "pagebreak"
  return "block"
}

/** The gaps the previous pass gave this paper's blocks, `index:px` pairs in
 *  `data-sheet-gaps` ON THE PAPER — ProseMirror ignores mutations of its own
 *  root's attributes, while an attribute on a block it manages makes it
 *  re-render that block (see `writeGaps`). */
function readGaps(paper: HTMLElement): Map<number, number> {
  const gaps = new Map<number, number>()
  for (const pair of (paper.dataset.sheetGaps ?? "").split(",")) {
    const [index, px] = pair.split(":").map(Number)
    if (Number.isFinite(index) && Number.isFinite(px)) gaps.set(index, px)
  }
  return gaps
}

/** Every top-level block's NATURAL geometry — where it would sit with no
 *  sheet gaps applied at all — in px below the paper's top edge.
 *
 *  The pass measures with its own previous output still in the DOM (one
 *  reflow instead of two), so what it applied is subtracted back out as it
 *  goes: the gap a block was given (`data-sheet-gaps`) and the
 *  spacer/repeated-head rows inserted inside a table both shift everything
 *  below them, and both are accounted for in `shift`. That is what makes
 *  the plan idempotent — re-measuring an already-paginated paper yields the
 *  same numbers as measuring a pristine one, so `planBreaks` returns the
 *  same breaks. */
export function measureBlocks(paper: HTMLElement): MeasuredBlock[] {
  const paperTop = paper.getBoundingClientRect().top
  // CSS `zoom` on an ancestor scales client rects, not offset sizes.
  const scale = paper.offsetWidth
    ? paper.getBoundingClientRect().width / paper.offsetWidth
    : 1
  const gaps = readGaps(paper)
  const blocks: MeasuredBlock[] = []
  let shift = 0
  for (const child of contentChildren(paper)) {
    shift += gaps.get(blocks.length) ?? 0
    const rect = child.getBoundingClientRect()
    const top = (rect.top - paperTop) / scale - shift
    const table = singleTableOf(child, scale)
    let rows: MeasuredRow[] | undefined
    let headHeight: number | undefined
    if (table) {
      rows = []
      for (const tr of table.querySelectorAll<HTMLElement>("tbody > tr")) {
        const row = tr.getBoundingClientRect()
        if (
          tr.classList.contains(GAP_ROW_CLASS) ||
          tr.classList.contains(HEAD_ROW_CLASS)
        ) {
          shift += row.height / scale
          continue
        }
        rows.push({
          top: (row.top - paperTop) / scale - shift,
          bottom: (row.bottom - paperTop) / scale - shift,
        })
      }
      const head = table.querySelector("thead")
      headHeight = head ? head.getBoundingClientRect().height / scale : 0
    }
    blocks.push({
      index: blocks.length,
      top,
      // After `shift`'s row-spacer updates: they sit inside this block.
      bottom: (rect.bottom - paperTop) / scale - shift,
      kind: kindOf(child),
      ...(rows?.length ? { rows, headHeight } : {}),
    })
  }
  return blocks
}

/** The one table a block breaks at row boundaries — only when the block has
 *  exactly one and it is laid out (a zero-height table carries no usable row
 *  rects, e.g. while the fragment is still rendering). */
function singleTableOf(el: HTMLElement, scale: number): HTMLElement | null {
  const tables = el.querySelectorAll<HTMLElement>("table")
  if (tables.length !== 1) return null
  const table = tables[0]
  return table.getBoundingClientRect().height / scale > 0 ? table : null
}

/** Paginates `paper` in place: measure → `planBreaks` → apply, then draw the
 *  bands over the sheet gaps. Writes only what the plan asks for, so a
 *  re-run on unchanged content reproduces exactly the same DOM.
 *
 *  Returns the number of sheets the content now occupies. */
export function layoutSheets(
  paper: HTMLElement,
  geometry: SheetGeometry,
  options: {
    showSheetCounter?: boolean
    /** Server-rendered page strips for a paper with no strip NODES (design
     *  §8, review I3) — see `SheetBandOptions.headerHtml`. */
    stripHtml?: { header?: string | null; footer?: string | null }
  } = {},
): { sheets: number } {
  const natural = measureBlocks(paper)
  const children = contentChildren(paper)
  const stride = geometry.pageHeightPx + geometry.gapPx

  // Sheet 1's content has to start at `topPx` like every other sheet's. On
  // screen it does not: `.paper`'s padding is a fixed 12mm and the header
  // strip is a real first child with editor chrome, while `topPx` is what
  // the PDF reserves for that strip (its printed height + the strip gap, or
  // 12mm with no strip). The whole content column is translated by the
  // difference — 0 in the common case — so the planner's model and the DOM
  // agree about sheet 1 too, and the strip ends up inside the top margin
  // area the stylesheet pulls it into.
  const first = natural.findIndex((block) => block.kind !== "strip")
  const offsetPx =
    first < 0 ? 0 : Math.round(geometry.topPx - natural[first].top)
  const blocks = offsetPx
    ? natural.map((block) => shifted(block, offsetPx))
    : natural

  const breaks = planBreaks(blocks, geometry)

  /** index → how far this pass moves that block down, its own record. */
  const gaps = new Map<number, number>()
  const addGap = (index: number, px: number): void => {
    gaps.set(index, (gaps.get(index) ?? 0) + px)
  }
  if (offsetPx && first >= 0) addGap(first, offsetPx)

  // Relative to `blocks`, which already carry the offset.
  const splits = new Map<number, RowSplit>()
  let shift = 0
  for (const brk of breaks) {
    const child = children[brk.atBlock]
    if (!child) continue
    const gapPx = Math.round(brk.gapPx)
    if ("atRow" in brk) {
      const block = blocks[brk.atBlock]
      splits.set(brk.atBlock, {
        atRow: brk.atRow,
        // The planner reserved `headHeight` of the gap for the head the
        // split repeats below the spacer.
        spacerPx: Math.max(0, gapPx - Math.round(block?.headHeight ?? 0)),
        rows: block?.rows?.length ?? 0,
      })
    } else {
      addGap(brk.atBlock, gapPx)
    }
    // Every break pushes everything below it — including the paper's end.
    shift += gapPx
  }
  applyRowSplits(paper, children, splits)

  const lastContent = blocks.filter((block) => block.kind !== "strip").pop()
  const contentEndPx = (lastContent?.bottom ?? 0) + shift
  const sheets = Math.max(1, Math.floor(contentEndPx / stride) + 1)

  // The real footer prints in the LAST sheet's bottom margin, exactly where
  // the clones sit on the sheets above it (`STRIP_GAP_PX` off the edge) —
  // not in flow under the last block.
  let paperEndPx = contentEndPx
  const footerIndex = children.findIndex((el) =>
    el.matches('[data-page-strip="pageFooter"]'),
  )
  const footer = footerIndex >= 0 ? blocks[footerIndex] : undefined
  if (footer) {
    const height = footer.bottom - footer.top
    const target =
      (sheets - 1) * stride + geometry.pageHeightPx - STRIP_GAP_PX - height
    const pin = Math.max(0, Math.round(target - (footer.top + shift)))
    if (pin) addGap(footerIndex, pin)
    paperEndPx = footer.top + shift + pin + height
  }

  writeGaps(paper, gaps, natural, children)

  const boundaries: number[] = []
  for (let k = 0; k < sheets - 1; k++) {
    boundaries.push(k * stride + geometry.pageHeightPx)
  }
  renderBands(paper, boundaries, geometry, {
    header: paper.querySelector<HTMLElement>('[data-page-strip="pageHeader"]'),
    footer: paper.querySelector<HTMLElement>('[data-page-strip="pageFooter"]'),
    headerHtml: options.stripHtml?.header,
    footerHtml: options.stripHtml?.footer,
    counter: options.showSheetCounter,
  })

  // The paper ends exactly at the last sheet's bottom edge, so that sheet is
  // a full sheet. (No 20mm floor: with a pinned footer the remaining padding
  // IS the rest of the bottom margin, and without one the content already
  // stops `bottomPx` above the edge.)
  const padding = `${Math.max(
    0,
    Math.round(sheets * stride - geometry.gapPx - paperEndPx),
  )}px`
  if (paper.style.paddingBottom !== padding) paper.style.paddingBottom = padding

  return { sheets }
}

/** Where a table is split, and how tall the spacer that does it is. */
interface RowSplit {
  atRow: number
  spacerPx: number
  /** Body rows the plan was made for — a changed table invalidates it. */
  rows: number
}

const splitKey = (split: RowSplit): string =>
  `${split.atRow}:${split.spacerPx}:${split.rows}`

/** Brings every table in the paper to the split the plan asks for (none, for
 *  most of them). A table already carrying exactly that split is left alone,
 *  so a pass that changes nothing writes nothing — which is what lets the
 *  observers re-run this to a fixed point instead of looping. */
function applyRowSplits(
  paper: HTMLElement,
  children: HTMLElement[],
  splits: Map<number, RowSplit>,
): void {
  for (const table of paper.querySelectorAll<HTMLElement>("table")) {
    const split = splits.get(children.findIndex((el) => el.contains(table)))
    const key = split ? splitKey(split) : ""
    if ((table.dataset.sheetSplit ?? "") === key) continue
    for (const row of table.querySelectorAll(
      `tr.${GAP_ROW_CLASS}, tr.${HEAD_ROW_CLASS}`,
    )) {
      row.remove()
    }
    if (!split) {
      table.removeAttribute("data-sheet-split")
      continue
    }
    splitRows(table, split)
    table.dataset.sheetSplit = key
  }
}

/** How much the space above block `index` has to grow to move it `gapPx`
 *  down. NOT `gapPx`: adjacent siblings' vertical margins COLLAPSE, so the
 *  space between two blocks is `max(previous margin-bottom, this
 *  margin-top)`, never their sum — injecting `gapPx` alone would lose
 *  whatever the two blocks already had between them (8px between
 *  paragraphs, 18px above an `.h2`) on every single break, and the error
 *  would compound down the document.
 *
 *  The natural distance is read off the measurements; the injected margin
 *  has to be that distance plus the gap. When the result is smaller than
 *  the previous block's own bottom margin (only for a negative gap, i.e.
 *  pulling content UP into the top margin), the collapse takes the maximum
 *  of the two positives, so the difference goes in as a negative margin,
 *  which collapsing adds instead. */
function marginFor(
  index: number,
  gapPx: number,
  natural: MeasuredBlock[],
  children: HTMLElement[],
  paperTopPx: number,
): number {
  const block = natural[index]
  if (!block) return gapPx
  const previous = natural[index - 1]
  const naturalGap = Math.max(
    0,
    block.top - (previous ? previous.bottom : paperTopPx),
  )
  const target = naturalGap + gapPx
  const previousMargin = previous
    ? parseFloat(getComputedStyle(children[index - 1]).marginBottom) || 0
    : 0
  return Math.round(target >= previousMargin ? target : target - previousMargin)
}

/** Gives the blocks their sheet gaps — as CSS rules in a stylesheet BESIDE
 *  the paper, keyed by the paper's own `data-sheet-layout` id.
 *
 *  Not as inline styles on the blocks themselves: those are ProseMirror's
 *  DOM, and any attribute written on one makes PM re-parse and re-render
 *  that node, which throws the margin away again (and, since the re-render
 *  replaces the element, re-triggers this pass — a loop). Rules in a
 *  stylesheet outside the editor touch nothing PM owns. Row spacers are
 *  safe as real DOM: they go inside locked NodeView atoms, whose mutations
 *  PM ignores. */
function writeGaps(
  paper: HTMLElement,
  gaps: Map<number, number>,
  natural: MeasuredBlock[],
  children: HTMLElement[],
): void {
  const serialized = Array.from(gaps, ([i, px]) => `${i}:${px}`).join(",")
  // An attribute on the paper (ProseMirror's root): ignored by PM, and the
  // record the next pass subtracts back out.
  if ((paper.dataset.sheetGaps ?? "") !== serialized) {
    if (serialized) paper.dataset.sheetGaps = serialized
    else paper.removeAttribute("data-sheet-gaps")
  }
  const host = paper.parentElement
  if (!host) return
  const existing = Array.from(host.children).find((el) =>
    el.classList.contains(GAP_STYLE_CLASS),
  )
  if (!gaps.size) {
    existing?.remove()
    return
  }
  if (!paper.dataset.sheetLayout) {
    paper.dataset.sheetLayout = String((paperSeq += 1))
  }
  // `.paper` + the id + `:nth-child` outweighs everything the print
  // stylesheet sets a top margin with (`.h2:first-of-type` is its most
  // specific), and this sheet comes after it in document order anyway.
  const scope = `.paper[data-sheet-layout="${paper.dataset.sheetLayout}"]`
  const paperTopPx = parseFloat(getComputedStyle(paper).paddingTop) || 0
  const css = Array.from(gaps, ([index, px]) => {
    const margin = marginFor(index, px, natural, children, paperTopPx)
    return `${scope} > :nth-child(${index + 1}) { margin-top: ${margin}px; }`
  }).join("\n")
  const style = existing ?? document.createElement("style")
  if (!existing) {
    style.className = GAP_STYLE_CLASS
    host.append(style)
  }
  if (style.textContent !== css) style.textContent = css
}

/** Moves a table's tail from `atRow` on onto the next sheet: a spacer row
 *  filling the rest of this sheet, then the table's head repeated above the
 *  tail. Both rows are inert clones inside a locked fragment's HTML, never
 *  part of the document tree. */
function splitRows(table: HTMLElement, split: RowSplit): void {
  const row = table.querySelectorAll<HTMLElement>("tbody > tr")[split.atRow]
  if (!row?.parentNode) return
  const headRow = table.querySelector<HTMLTableRowElement>("thead > tr")

  const spacer = document.createElement("tr")
  spacer.className = GAP_ROW_CLASS
  const cell = document.createElement("td")
  cell.colSpan = headRow?.children.length ?? row.children.length
  cell.style.height = `${split.spacerPx}px`
  spacer.append(cell)
  row.parentNode.insertBefore(inert(spacer), row)

  if (headRow) {
    const clone = headRow.cloneNode(true) as HTMLTableRowElement
    clone.className = HEAD_ROW_CLASS
    row.parentNode.insertBefore(inert(clone), row)
  }
}

function inert<T extends HTMLElement>(el: T): T {
  el.setAttribute("contenteditable", "false")
  el.setAttribute("aria-hidden", "true")
  return el
}
